import { createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { TRANSACTION_TYPE_DICT } from '~/pages/leases/lease-details/components/lease-deposit-transaction/transactionConfigs';
import { TransactionsRepository } from '~/repositories/transactionRepository';
import { LeaseChargeLedgerEntryType } from '~/swagger/portal/Api';
import type {
  AddLeaseChargeCreditDto,
  AddLeaseChargeDto,
  AddLeaseChargeFilesDto,
  AddLeaseChargePaymentDto,
  AdjustLeaseChargeCreditDto,
  AdjustLeaseChargeDto,
  AdjustLeaseChargePaymentDto,
  ChartOfAccountDto,
  LeaseChargeLedgerEntryDto,
  ReleaseLeaseBalanceDto,
  RemoveLeaseChargeFilesDto,
  ReturnLeaseChargePaymentDto,
  UpdateLeaseChargeFilesDto,
  UpdateVendorBillDto,
} from '~/swagger/portal/Api';
export const [LeaseRentTransactionsProvider, useLeaseRentTransactions] = createMagicDoorContext('LeaseRentTransactions', () => {
  const repo = new TransactionsRepository();
  const [leaseId, setLeaseId] = createSignal<string>('');
  const [transactions, setTransactions] = createSignal<LeaseChargeLedgerEntryDto[]>([]);
  const [chartOfAccounts, setChartOfAccounts] = createSignal<ChartOfAccountDto[]>([]);
  const [currentBalance, setCurrentBalance] = createSignal<number>();
  const [loading, setLoading] = createSignal<boolean>(false);
  const [error, setError] = createSignal<Error | undefined>(undefined);

  const getLeaseRentTransactions = async (id: string, take?: number) => {
    setLoading(true);
    try {
      setLeaseId(id);
      const response = await repo.getLeaseRentTransactions(id, take);
      setTransactions(response.entries || []);
      setChartOfAccounts(response.chartOfAccounts || []);
      setCurrentBalance(response.currentBalance);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const setupCharge = async (id: string, payload: AddLeaseChargeDto): Promise<void> => {
    try {
      await repo.setupCharge(id, payload);
      getLeaseRentTransactions(id);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const offlinePayment = async (id: string, payload: AddLeaseChargePaymentDto): Promise<void> => {
    try {
      await repo.offlinePayment(id, payload);
      getLeaseRentTransactions(id);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const addCredit = async (id: string, payload: AddLeaseChargeCreditDto): Promise<void> => {
    try {
      await repo.addCredit(id, payload);
      getLeaseRentTransactions(id);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const deleteTransaction = async (leaseId: string, transactionId: string): Promise<boolean | undefined> => {
    try {
      await repo.deleteTransaction(transactionId);
      getLeaseRentTransactions(leaseId);
      return true;
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const [lateFee, getLateFee] = createTriggerResource(async (payload: { leaseId: string; lateFeeId: string }) => {
    return await repo.getLateFee(payload.leaseId, payload.lateFeeId);
  });

  const deleteLateFee = createMutation(async (payload: { leaseId: string; lateFeeId: string }) => {
    await repo.deleteLateFee(payload.leaseId, payload.lateFeeId);
    await getLeaseRentTransactions(payload.leaseId);
  });

  const deleteBill = async (leaseId: string, chargeId: string): Promise<boolean | undefined> => {
    try {
      await repo.deleteBill(chargeId);
      getLeaseRentTransactions(leaseId);
      return true;
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const payoutTenant = async (leaseId: string, request: ReleaseLeaseBalanceDto) => {
    try {
      const transaction = await repo.payoutTenant(leaseId, request);
      getLeaseRentTransactions(leaseId);
      return transaction;
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const markAsReturned = async (leaseId: string, paymentId: string, payload: ReturnLeaseChargePaymentDto): Promise<void> => {
    try {
      await repo.markAsReturned(leaseId, paymentId, payload);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const updateTransaction = async (leaseId: string, chargeId: string, payload: AdjustLeaseChargeDto) => {
    await repo.updateTransaction(leaseId, chargeId, payload);
  };

  const updateBill = async (billId: string, payload: UpdateVendorBillDto) => {
    await repo.updateBill(billId, payload);
  };

  const getTransaction = createMutation(async (params: { leaseId: string; transactionId: string; type: LeaseChargeLedgerEntryType }) => {
    const { leaseId, transactionId, type } = params;
    switch (type) {
      case LeaseChargeLedgerEntryType.Payment:
      case LeaseChargeLedgerEntryType.Return:
      case LeaseChargeLedgerEntryType.Payout:
        return await repo.getChargePayment(leaseId, transactionId);
      case LeaseChargeLedgerEntryType.Transfer:
        return await repo.getChargeTransfer(leaseId, transactionId);
      case LeaseChargeLedgerEntryType.Charge:
        return await repo.getCharge(leaseId, transactionId);
      case LeaseChargeLedgerEntryType.LateFee:
        return await repo.getLateFee(leaseId, transactionId);
      case LeaseChargeLedgerEntryType.Credit:
        return await repo.getChargeCredit(leaseId, transactionId);
      default:
        return undefined;
    }
  });

  const batchAddOfflinePayment = async (id: string, payload: AddLeaseChargePaymentDto): Promise<void> => {
    try {
      await repo.offlinePayment(id, payload, false);
    } catch (err: any) {
      setError(err);
      throw err;
    }
  };

  const [charge, getCharge] = createTriggerResource(async (payload: { leaseId: string; chargeId: string }) => {
    return await repo.getCharge(payload.leaseId, payload.chargeId);
  });

  const getChargeById = createMutation(async (leaseId: string, chargeId: string) => {
    return await repo.getCharge(leaseId, chargeId);
  });

  const deleteCharge = createMutation(async (payload: { leaseId: string; chargeId: string }) => {
    await repo.deleteCharge(payload.leaseId, payload.chargeId);
    await getLeaseRentTransactions(payload.leaseId);
  });

  const deleteCredit = createMutation(async (payload: { leaseId: string; creditId: string }) => {
    await repo.deleteCredit(payload.leaseId, payload.creditId);
    await getLeaseRentTransactions(payload.leaseId);
  });

  const [chargeCredit, getChargeCredit] = createTriggerResource(async (payload: { leaseId: string; creditId: string }) => {
    return await repo.getChargeCredit(payload.leaseId, payload.creditId);
  });

  const getChargeCreditById = createMutation(async (leaseId: string, creditId: string) => {
    return await repo.getChargeCredit(leaseId, creditId);
  });

  const updateChargeCredit = createMutation(async (payload: { leaseId: string; creditId: string; credit: AdjustLeaseChargeCreditDto }) => {
    const result = await repo.updateChargeCredit(payload.leaseId, payload.creditId, payload.credit);
    await getLeaseRentTransactions(payload.leaseId);
    return result;
  });

  const updateCharge = createMutation(async (payload: { leaseId: string; chargeId: string; charge: AdjustLeaseChargeDto }) => {
    const result = await repo.updateTransaction(payload.leaseId, payload.chargeId, payload.charge);
    await getLeaseRentTransactions(payload.leaseId);
    return result;
  });

  const addChargeFiles = createMutation(async (leaseId: string, chargeId: string, files: AddLeaseChargeFilesDto) => {
    return await repo.addChargeFiles(leaseId, chargeId, files);
  });

  const uploadChargeFiles = createMutation(async (leaseId: string, chargeId: string, files: File[]) => {
    return await repo.uploadChargeFiles(leaseId, chargeId, files);
  });

  const updateChargeFiles = createMutation(
    async (payload: { leaseId: string; chargeId: string; fileId: string; files: UpdateLeaseChargeFilesDto }) => {
      const result = await repo.updateChargeFiles(payload.leaseId, payload.chargeId, payload.fileId, payload.files);
      await getLeaseRentTransactions(payload.leaseId);
      return result;
    }
  );

  const deleteChargeFiles = createMutation(async (leaseId: string, chargeId: string, fileIds: RemoveLeaseChargeFilesDto) => {
    return await repo.deleteChargeFiles(leaseId, chargeId, fileIds);
  });

  const [chargePayment, getChargePayment] = createTriggerResource(async (payload: { leaseId: string; paymentId: string }) => {
    return await repo.getChargePayment(payload.leaseId, payload.paymentId);
  });

  const getChargePaymentById = createMutation(async (leaseId: string, paymentId: string) => {
    return await repo.getChargePayment(leaseId, paymentId);
  });

  const updateChargePayment = createMutation(
    async (payload: { leaseId: string; paymentId: string; payment: AdjustLeaseChargePaymentDto }) => {
      const result = await repo.updateChargePayment(payload.leaseId, payload.paymentId, payload.payment);
      await getLeaseRentTransactions(payload.leaseId);
      return result;
    }
  );

  const deleteChargePayment = createMutation(async (payload: { leaseId: string; paymentId: string }) => {
    await repo.deleteChargePayment(payload.leaseId, payload.paymentId);
    await getLeaseRentTransactions(payload.leaseId);
  });

  const [chargeTransfer, getChargeTransfer] = createTriggerResource(async (payload: { leaseId: string; transferId: string }) => {
    return await repo.getChargeTransfer(payload.leaseId, payload.transferId);
  });

  const deleteChargeTransfer = createMutation(async (payload: { leaseId: string; transferId: string }) => {
    await repo.deleteChargeTransfer(payload.leaseId, payload.transferId);
    await getLeaseRentTransactions(payload.leaseId);
  });

  const deleteStrategiesDict = TRANSACTION_TYPE_DICT.cloneAndExtend((item) => {
    const deleteFnMap: Record<LeaseChargeLedgerEntryType, (leaseId: string, id: string) => Promise<void>> = {
      [LeaseChargeLedgerEntryType.Charge]: async (leaseId, id) => deleteCharge({ leaseId, chargeId: id }),
      [LeaseChargeLedgerEntryType.LateFee]: async (leaseId, id) => deleteLateFee({ leaseId, lateFeeId: id }),
      [LeaseChargeLedgerEntryType.Credit]: async (leaseId, id) => deleteCredit({ leaseId, creditId: id }),
      [LeaseChargeLedgerEntryType.Payment]: async (leaseId, id) => deleteChargePayment({ leaseId, paymentId: id }),
      [LeaseChargeLedgerEntryType.Return]: async (leaseId, id) => deleteChargePayment({ leaseId, paymentId: id }),
      [LeaseChargeLedgerEntryType.Transfer]: async (leaseId, id) => deleteChargeTransfer({ leaseId, transferId: id }),
      [LeaseChargeLedgerEntryType.Payout]: async (leaseId, id) => deleteChargePayment({ leaseId, paymentId: id }),
    };
    item.meta!.deleteFn = deleteFnMap[item.value];
    return item;
  });

  const deleteByType = async (leaseId: string, type: LeaseChargeLedgerEntryType, id: string) => {
    const meta = deleteStrategiesDict.getMetaByValue(type);
    if (!meta?.deleteFn) {
      throw new Error(`Unknown transaction type: ${type}`);
    }
    await meta.deleteFn(leaseId, id);
  };

  const getTypeLabelByValue = (type: LeaseChargeLedgerEntryType | undefined) => {
    return deleteStrategiesDict.getLabelByValue(type);
  };

  return {
    leaseId,
    transactions,
    chartOfAccounts,
    loading,
    error,
    getLeaseRentTransactions,
    payoutTenant,
    setupCharge,
    offlinePayment,
    addCredit,
    deleteTransaction,
    lateFee,
    getLateFee,
    deleteLateFee,
    deleteBill,
    markAsReturned,
    updateTransaction,
    updateBill,
    getTransaction,
    batchAddOfflinePayment,
    currentBalance,
    charge,
    getCharge,
    getChargeById,
    updateCharge,
    deleteCharge,
    deleteCredit,
    chargeCredit,
    getChargeCredit,
    getChargeCreditById,
    updateChargeCredit,
    addChargeFiles,
    uploadChargeFiles,
    updateChargeFiles,
    deleteChargeFiles,
    chargePayment,
    getChargePayment,
    getChargePaymentById,
    updateChargePayment,
    deleteChargePayment,
    chargeTransfer,
    getChargeTransfer,
    deleteChargeTransfer,
    deleteByType,
    getTypeLabelByValue,
  };
});
